import { useState, useCallback, useEffect } from 'react';
import { parcellingSchema, type ParcellingFormData } from '../schemas';
import { type ParcelamentoAPI, type ParcelData } from '../types';
import { createEmptyParcelData, fromFixedParcelPayload } from '../utils';
import { NUTRIENT_NAMES } from '../constants';
import type { z } from 'zod';

interface UseParcellingFormOptions {
	initialData?: ParcelamentoAPI;
	onSubmit?: (data: ParcellingFormData) => void | Promise<void>;
	onError?: (error: string) => void;
}

const IDENTIFICATION_FIELD_LABELS: Record<string, string> = {
	name: 'Identificação',
	culture: 'Cultura',
	cropStage: 'Estágio da Cultura',
	note: 'Observação',
};

interface ClassifiedErrors {
	fieldErrors: Record<string, string>;
	identificationLabels: string[];
	hasPhaseError: boolean;
	invalidNutrients: string[];
}

const classifyParcelError = (
	err: z.ZodIssue,
	result: ClassifiedErrors,
) => {
	const subField = err.path?.[1] as string | undefined;
	const errorKey = subField ? `parcels.${subField}` : 'parcels';
	if (result.fieldErrors[errorKey]) return;

	if (subField === 'phase') {
		result.hasPhaseError = true;
		result.fieldErrors[errorKey] = 'Selecione a fase para todos os meses da tabela';
		return;
	}

	const nutrientName = subField ? NUTRIENT_NAMES[subField as keyof typeof NUTRIENT_NAMES] : undefined;
	if (!subField || !nutrientName) {
		result.fieldErrors[errorKey] = 'Preencha todos os dados do parcelamento';
		return;
	}

	result.invalidNutrients.push(subField);

	if (err.path.length === 2) {
		result.fieldErrors[errorKey] = err.message;
		return;
	}

	result.fieldErrors[errorKey] = `Preencha todos os valores de ${nutrientName} (${subField})`;
};

const classifyErrors = (issues: z.ZodIssue[]): ClassifiedErrors => {
	const result: ClassifiedErrors = {
		fieldErrors: {},
		identificationLabels: [],
		hasPhaseError: false,
		invalidNutrients: [],
	};

	for (const err of issues) {
		const field = err.path?.[0] as string | undefined;
		if (!field) continue;

		if (field === 'parcels') {
			classifyParcelError(err, result);
		} else if (!result.fieldErrors[field]) {
			result.fieldErrors[field] = err.message;
			if (IDENTIFICATION_FIELD_LABELS[field]) {
				result.identificationLabels.push(IDENTIFICATION_FIELD_LABELS[field]);
			}
		}
	}

	return result;
};

const dispatchToasts = (
	classified: ClassifiedErrors,
	onError: (msg: string) => void,
) => {
	const { identificationLabels, fieldErrors, hasPhaseError, invalidNutrients } = classified;

	if (identificationLabels.length === 1) {
		const key = Object.keys(IDENTIFICATION_FIELD_LABELS).find((k) => fieldErrors[k]);
		if (key) onError(fieldErrors[key]);
	} else if (identificationLabels.length > 1) {
		onError(`Preencha os campos: ${identificationLabels.join(', ')}`);
	}

	if (hasPhaseError) {
		onError('Selecione a fase para todos os meses da tabela');
	}

	if (invalidNutrients.length === 1) {
		onError(fieldErrors[`parcels.${invalidNutrients[0]}`]);
	} else if (invalidNutrients.length > 1) {
		onError(`Verifique a distribuição dos nutrientes: ${invalidNutrients.join(', ')}`);
	}
};

export const useParcellingForm = ({
	initialData,
	onSubmit,
	onError,
}: UseParcellingFormOptions = {}) => {
	const getInitialFormData = (): ParcellingFormData => {
		if (initialData) {
			return {
				name: initialData.name,
				culture: initialData.culture,
				cropStage: initialData.cropStage,
				note: initialData.note || '',
				bindingUserIds: initialData.bindingUserIds || [],
				farmIds: initialData.farmIds || [],
				isPublic: initialData.isPublic ?? false,
				parcels: fromFixedParcelPayload(initialData.fixedParcel),
			};
		}

		return {
			name: '',
			culture: '' as ParcellingFormData['culture'],
			cropStage: '' as ParcellingFormData['cropStage'],
			note: '',
			bindingUserIds: [],
			farmIds: [],
			isPublic: false,
			parcels: createEmptyParcelData(),
		};
	};

	const [formData, setFormData] = useState<ParcellingFormData>(getInitialFormData);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isDirty, setIsDirty] = useState(false);

	useEffect(() => {
		if (initialData) {
			setFormData(getInitialFormData());
		}
	}, [initialData]);

	const updateField = useCallback(
		<K extends keyof ParcellingFormData>(field: K, value: ParcellingFormData[K]) => {
			setFormData((prev) => ({ ...prev, [field]: value }));
			setIsDirty(true);
			if (errors[field]) {
				setErrors((prev) => {
					const newErrors = { ...prev };
					delete newErrors[field];
					return newErrors;
				});
			}
		},
		[errors]
	);

	const updateParcels = useCallback((parcels: ParcelData) => {
		setFormData((prev) => ({ ...prev, parcels }));
		setIsDirty(true);
	}, []);

	const validate = useCallback((): boolean => {
		const result = parcellingSchema.safeParse(formData);

		if (result.success) {
			setErrors({});
			return true;
		}

		const classified = classifyErrors(result.error.issues);
		setErrors(classified.fieldErrors);

		if (onError) {
			dispatchToasts(classified, onError);
		}

		return false;
	}, [formData, onError]);

	const handleSubmit = useCallback(
		async (e?: React.FormEvent) => {
			if (e) {
				e.preventDefault();
			}

			if (!validate()) {
				return;
			}

			setIsSubmitting(true);
			try {
				await onSubmit?.(formData);
				setIsDirty(false);
			} catch (err) {
				const message =
					err instanceof Error
						? err.message
						: 'Erro inesperado ao salvar parcelamento. Tente novamente.';
				onError?.(message);
			} finally {
				setIsSubmitting(false);
			}
		},
		[formData, validate, onSubmit, onError]
	);

	const reset = useCallback(() => {
		setFormData(getInitialFormData());
		setErrors({});
		setIsDirty(false);
	}, [initialData]);

	return {
		formData,
		errors,
		isSubmitting,
		isDirty,
		updateField,
		updateParcels,
		handleSubmit,
		validate,
		reset,
	};
};
